import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { setLoanGroupData } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { InputType } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getGenerateDocuments, getServicingNotes, postServicingNotes, updateLoanFields } from 'services'
import { ButtonGroup, Toggle } from 'stories/components'
import { OverrideCaclModal } from 'stories/components/OverrideCalcModal/OverrideCalcModal'
import { Tooltip } from 'stories/components/Tooltip/Tooltip'
import { confirm, formatDateYMD, openAuditLog } from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { calcKeys } from './constants'

const tabs = ['Loan Details', 'Insurance', 'Taxes']

export const ServicingNote = () => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<any>({})
  const [orgData, setOrgData] = useState<any>({})
  const [generateDocuments, setGenerateDocuments] = useState<Record<string, any>>({})
  const [ovData, setOVData] = useState<any>({})
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [insuranceInputs, setInsuranceInputs] = useState<Record<string, InputType>>({})
  const [taxInputs, setTaxInputs] = useState<Record<string, InputType>>({})
  const [extensionInputs, setExtensionInputs] = useState<Record<string, InputType>>({})
  const [tab, setTab] = useState(tabs[0])

  const dispatch = useDispatch()

  const { loan, loanDetail } = useSelector((state: any) => {
    return {
      loan: state.loan,
      loanDetail: state.loanDetail,
    }
  })

  useEffect(() => {
    setLoading(true)
    setLoanNumber()
    getServicingNotes()
      .then((res: any) => {
        if (res.success) {
          setData(res.data)
          setOrgData(res.data)
          setInputs(res.inputs)
          setInsuranceInputs(res.insuranceInputs)
          setTaxInputs(res.taxInputs)
          setExtensionInputs(res.extensionInputs)
        }
      })
      .finally(() => setLoading(false))
    getGenerateDocuments(true).then(({ data }) => {
      setGenerateDocuments(data)
    })
  }, [])

  const tabInputs = useMemo(() => {
    let rtl: string[] = []
    const inputKeys = Object.keys(inputs)
    const insInputKeys = ['insuranceSection', ...Object.keys(insuranceInputs)]
    const taxInputKeys = ['taxSection', ...Object.keys(taxInputs)]
    switch (tab) {
      case 'Loan Details':
        rtl = inputKeys.filter((v) => ![...insInputKeys, ...taxInputKeys].includes(v))
        break
      case 'Insurance':
        rtl = insInputKeys
        break
      case 'Taxes':
        rtl = taxInputKeys
        break
      default:
        break
    }
    return rtl
  }, [inputs, insuranceInputs, taxInputs, tab])

  const onChange = async (key: string, value: string) => {
    if (loading) return
    const temp = cloneDeep(data)
    temp[key] = value
    setData(temp)
  }

  const onBlur = async (key: string, _value?: any) => {
    const value = data[key]
    if (_value == undefined) {
      if (
        (typeof value === 'string' && value === orgData[key]) ||
        (typeof value === 'object' && JSON.stringify(value) === JSON.stringify(orgData[key]))
      )
        return
    }
    setLoading(true)
    await postServicingNotes({
      key,
      value: _value == undefined ? value : _value,
    })
    setOrgData(data)
    setLoading(false)
  }

  const showHistory = async (key: string, fieldLabel?: string) => {
    const options = {
      table: 'Servicing Information',
      field: key,
      fieldLabel,
      keys: {
        field: key,
      },
      renderValue: (data: any) => <span dangerouslySetInnerHTML={{ __html: data }} />,
    }
    openAuditLog(options)
  }

  const fillRate = () => {
    const rate = loanDetail.rateData.rate
    const temp = cloneDeep(data)
    temp['originalInterestRate'] = rate
    setData(temp)
    onBlur('originalInterestRate', rate)
  }

  const fillDefaultRate = () => {
    const temp = cloneDeep(data)
    temp['defaultInterestRate'] = generateDocuments.defaultRate
    setData(temp)
    onBlur('defaultInterestRate', generateDocuments.defaultRate)
  }

  const onLoanFieldChange = (key: string, value: any) => {
    const json: Record<string, any> = { [key]: value }
    updateLoanFields(json).then(() => {
      dispatch(setLoanGroupData(json))
    })
  }

  const closeOVModal = async (save: false, data: any) => {
    if (save) {
      onChange(ovData.overrideKey, data.ovValue)
      await onBlur(ovData.overrideKey, data.ovValue)
    }
    setOVData({})
  }

  const onAddAdditionalInfo = (key: string) => {
    const temp = cloneDeep(data)
    const addonKey =
      key === 'insuranceSection'
        ? 'additionalInsurance'
        : key === 'taxSection'
        ? 'additionalTax'
        : 'additionalExtension'
    if (!temp[addonKey]) temp[addonKey] = []
    temp[addonKey].push({ id: Date.now() })
    setData(temp)
    onBlur(addonKey, temp[addonKey])
  }

  const onRemoveAdditionalInfo = async (key: string, index: number) => {
    const isContinue = await confirm(
      `Are you sure you want to remove this additional ${
        key === 'additionalInsurance' ? 'Insurance' : 'Tax'
      } Information?`,
    )
    if (!isContinue) return
    const temp = cloneDeep(data)
    if (!temp[key]) return
    temp[key].splice(index, 1)
    setData(temp)
    onBlur(key, temp[key])
  }

  const onSubChange = (key: string, subKey: string, index: number, value: any) => {
    if (loading) return
    const temp = cloneDeep(data)
    if (!temp[key] || !temp[key][index]) return
    temp[key][index][subKey] = value
    setData(temp)
  }

  return (
    <div className="servicing-note-container">
      <LayoutLoading show={loading} />
      <ButtonGroup title={tabs} value={tab} onChange={setTab} />
      <div className="w-full">
        <div className="grid grid-cols-3 gap-2">
          {Object.keys(inputs).map((key, index) => {
            if (!tabInputs.includes(key)) return
            const input = inputs[key]
            const overrideKey = `${key}OV`
            input.value = data[overrideKey] || data[key]
            if (input.type === 'date' && input.value?.length > 0) {
              input.value = formatDateYMD(input.value)
            }
            input.history = true

            if (data[overrideKey]) input.bgColor = 'red-50'
            else input.bgColor = ''

            if (calcKeys.includes(key)) {
              input.disabled = true
              input.additionalElements = (
                <span
                  className="mx-2 text-shade-blue cursor-pointer"
                  onClick={() => {
                    setOVData({
                      title: input.title,
                      overrideKey,
                      calcValue: data[key] || '',
                      ovValue: data[overrideKey] || '',
                    })
                  }}
                >
                  <PencilSquareIcon className="w-4 h-4" />
                </span>
              )
            }

            if (key === 'originalInterestRate')
              input.additionalElements = (
                <span
                  className="mx-2 text-shade-blue hover:underline cursor-pointer font-variation-settings-600"
                  onClick={fillRate}
                >
                  <Tooltip message="Fill Original Interest Rate">
                    <span>Fill</span>
                  </Tooltip>
                </span>
              )
            if (key === 'defaultInterestRate')
              input.additionalElements = (
                <span
                  className="mx-2 text-shade-blue hover:underline cursor-pointer font-variation-settings-600"
                  onClick={fillDefaultRate}
                >
                  <Tooltip message="Fill Original Interest Rate">
                    <span>Fill</span>
                  </Tooltip>
                </span>
              )
            if (['servicerLoanNumber', 'servicerLoanNumber2', 'byteLoanNumber'].includes(key)) {
              const activeFci = key === 'servicerLoanNumber' ? 0 : key === 'servicerLoanNumber2' ? 1 : 2
              input.additionalElements = (
                <Toggle
                  id={`active-fci-${activeFci}`}
                  title=""
                  value={loan.activeFci === activeFci}
                  onChange={() => onLoanFieldChange('activeFci', activeFci)}
                />
              )
            }

            if (['insuranceSection', 'taxSection', 'extensionSection'].includes(key)) {
              input.additionalElements = (
                <div className="flex justify-end">
                  <span
                    className="text-shade-blue cursor-pointer hover:underline"
                    onClick={() => onAddAdditionalInfo(key)}
                  >
                    + Add
                  </span>
                </div>
              )
            }
            const rtl = [
              <div key={index} className={`col-span-${input.span || 1}`}>
                <RenderInput
                  input={input}
                  Key={key}
                  onChange={(key: string, value: any) => onChange(key, value)}
                  onBlur={(key: string) => onBlur(key)}
                  showHistory={() => showHistory(key, input.title)}
                />
              </div>,
            ]

            const insuranceInputKeys = Object.keys(insuranceInputs)
            const taxInputKeys = Object.keys(taxInputs)
            const extensionInputKeys = Object.keys(extensionInputs)

            const lastInsuranceField = insuranceInputKeys[insuranceInputKeys.length - 1]
            const lastTaxField = taxInputKeys[taxInputKeys.length - 1]
            const lastExtensionField = extensionInputKeys[extensionInputKeys.length - 1]

            if ([lastInsuranceField, lastTaxField, lastExtensionField].includes(key)) {
              const addonKey =
                key === lastInsuranceField
                  ? 'additionalInsurance'
                  : key === lastTaxField
                  ? 'additionalTax'
                  : 'additionalExtension'
              if (data[addonKey]?.length) {
                data[addonKey].map((item: any, subIndex: number) => {
                  const subInputs =
                    key === lastInsuranceField
                      ? cloneDeep(insuranceInputs)
                      : key === lastTaxField
                      ? cloneDeep(taxInputs)
                      : cloneDeep(extensionInputs)
                  rtl.push(
                    <div
                      key={`${addonKey}-${item.id}-header`}
                      className="flex justify-end gap-2 pb-2 items-center col-span-full border-b-[1px] border-gray-500 border-solid"
                    >
                      <RenderInput
                        input={{
                          inputType: 'toggle',
                          title: 'Active',
                          value: item.isActive,
                        }}
                        Key={`addonKey-isActive-${subIndex}`}
                        onChange={(key: string, value: any) => onSubChange(addonKey, 'isActive', subIndex, value)}
                        onBlur={() => onBlur(addonKey)}
                      />
                      <span
                        className="text-red-500 flex items-center gap-2 cursor-pointer hover:underline"
                        onClick={() => onRemoveAdditionalInfo(addonKey, subIndex)}
                      >
                        <TrashIcon className="w-4 h-4" />
                        Delete
                      </span>
                    </div>,
                  )
                  Object.keys(subInputs).map((subKey: string) => {
                    const subInput = (subInputs as any)[subKey]
                    subInput.value = item[subKey] || ''
                    subInput.history = true

                    rtl.push(
                      <div
                        key={`${addonKey}-${item.id}-${subKey}`}
                        className={`col-span-${subInput.span || 1} ${!item.isActive ? 'opacity-50' : ''}`}
                      >
                        <RenderInput
                          input={subInput}
                          Key={`${addonKey}-${item.id}-${subKey}`}
                          onChange={(key: string, value: any) => onSubChange(addonKey, subKey, subIndex, value)}
                          onBlur={() => onBlur(addonKey)}
                          showHistory={() =>
                            showHistory(`${addonKey}-${item.id}-${subKey}`, `${subInput.title} - ${subIndex + 2}`)
                          }
                        />
                      </div>,
                    )
                  })
                })
              }
            }
            return rtl
          })}
        </div>
      </div>
      {Object.keys(ovData).length > 0 && (
        <OverrideCaclModal
          title={ovData.title}
          calcValue={ovData.calcValue}
          ovValue={ovData.ovValue}
          onClose={closeOVModal}
        />
      )}
    </div>
  )
}
